// App
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers';

// Actions
import {
  FilterActions_handleFilterChange,
  FilterActions_handleReduxOnlyFilterChange,
} from 'app/shared/flux/actions/FilterActions';
import AppActions from 'app/shared/flux/actions/AppActions';

// Lodash
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';

// Constants
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';

// Tracking

// Misc / utils
import { adapt_reduxToQuery } from 'app/shared/flux/actions/FilterActions/adapters';
import type { FilterState } from 'app/types/filter.type';

// Pure utility functions (exported for testing)

/**
 * Checks if an index is within valid bounds for keyboard navigation
 */
export const isIndexInBounds = (idx: number, maxLength: number): boolean => {
  const minIndex = -1;
  const maxIndex = maxLength;
  return idx >= minIndex && idx < maxIndex;
};

/**
 * Sanitizes a filter object by removing empty strings and nil values
 */
export const sanitizeFilterForComparison = (filter: Record<string, unknown>): Record<string, unknown> => {
  return omitBy(filter, (v) => v === '' || isNil(v));
};

/**
 * Determines if filtered results should be fetched based on current state
 */
export const shouldFetchResults = (filter: FilterState, urlQuery: Record<string, unknown>): boolean => {
  const adaptedFilter = adapt_reduxToQuery({ filter });
  const sanitizedAdaptedFilter = sanitizeFilterForComparison(adaptedFilter);
  const adaptedUrlQuery = omit(urlQuery, ['lat', 'lon', 'z']);
  return !isEqual(adaptedUrlQuery, sanitizedAdaptedFilter);
};

// Hooks

type UseKeyboardNavigationProps = {
  focusRef: React.RefObject<HTMLElement>;
  refList: Array<React.RefObject<HTMLElement>>;
  showDropdown: boolean;
  toggleDropdown: (show: boolean) => void;
};

export const useKeyboardNavigation = ({
  focusRef,
  refList,
  showDropdown,
  toggleDropdown,
}: UseKeyboardNavigationProps): [(keyCode: number) => void, (keyCode: number) => void, () => void] => {
  const [currentIdx, setCurrentIdx] = useState(-1);

  const _outOfBoundsGuard = (idx: number): boolean => {
    return isIndexInBounds(idx, refList.length);
  };

  const _moveUpList = () => {
    const newIdx = currentIdx - 1;
    setCurrentIdx(newIdx);
    toggleDropdown(true);
    const shouldDecrement = _outOfBoundsGuard(newIdx);
    if (shouldDecrement && newIdx >= 0) {
      refList[newIdx].current?.focus();
    }

    if (newIdx < 0) {
      focusRef.current?.focus();
      toggleDropdown(false);
    }
  };

  const _moveDownList = () => {
    const newIdx = currentIdx + 1;
    const shouldIncrement = _outOfBoundsGuard(newIdx);

    if (shouldIncrement) {
      setCurrentIdx(newIdx);
      toggleDropdown(true);
      refList[newIdx].current?.focus();
    }
  };

  const basicKeyDownNav = (keyCode: number): void => {
    switch (keyCode) {
      case VALUE_CONSTANTS.DOWN_ARROW_KEYCODE:
        if (!showDropdown) {
          toggleDropdown(true);
        } else {
          _moveDownList();
        }
        break;
      case VALUE_CONSTANTS.ESC_KEYCODE:
        toggleDropdown(false);
    }

    return;
  };

  const dropdownKeyDownNav = (keyCode: number): void => {
    switch (keyCode) {
      case VALUE_CONSTANTS.ESC_KEYCODE:
        toggleDropdown(false);
        focusRef.current?.focus();
        break;
      case VALUE_CONSTANTS.UP_ARROW_KEYCODE:
        _moveUpList();
        break;
      case VALUE_CONSTANTS.DOWN_ARROW_KEYCODE:
        _moveDownList();
        break;
    }
    return;
  };

  const resetCurrentIdx = () => {
    setCurrentIdx(-1);
  };

  return [basicKeyDownNav, dropdownKeyDownNav, resetCurrentIdx];
};

type FilterChangeArgsObject = {
  preventDefault?: boolean;
  [key: string]: unknown;
};

type FilterChangeArgs = FilterChangeArgsObject | null | undefined;

export const useFilterChange = () => {
  const dispatch = useDispatch();
  const filter = useSelector((state: RootReduxState) => state.filter);
  const urlQuery = useSelector((state: RootReduxState) => state.location.current.query);
  const [isFilterResetting, setIsFilterResetting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedFetch = useMemo(
    () =>
      debounce((args: FilterChangeArgsObject, abortControllerSignal?: AbortSignal) => {
        dispatch(FilterActions_handleFilterChange(args, abortControllerSignal));
      }, 1000),
    [dispatch],
  );

  const debouncedFilterChange = useCallback(
    (args: FilterChangeArgsObject) => {
      // If AbortController already exists, then abort! (i.e. user has clicked a filter again)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Instantiate a new AbortController
      abortControllerRef.current = new AbortController();
      debouncedFetch(args, abortControllerRef.current.signal);
    },
    [debouncedFetch],
  );

  const filterChange = (args: FilterChangeArgsObject) => dispatch(FilterActions_handleFilterChange(args, undefined));

  const reduxOnlyChange = (args: FilterChangeArgsObject) => dispatch(FilterActions_handleReduxOnlyFilterChange(args));

  const resetFilter = async (args?: FilterChangeArgs) => {
    const preventDefault = args?.preventDefault ?? false;
    setIsFilterResetting(true);
    const action = preventDefault
      ? FilterActions_handleReduxOnlyFilterChange(args)
      : FilterActions_handleFilterChange(args, undefined);

    await dispatch(action);

    if (!args) {
      dispatch(
        AppActions.toggleNotificationMessage({
          content: 'Your filters have been reset.',
        }),
      );
    }
    setIsFilterResetting(false);
  };

  const shouldFetchFilteredResults = () => {
    return shouldFetchResults(filter, urlQuery);
  };

  return {
    debouncedFilterChange,
    filterChange,
    reduxOnlyChange,
    resetFilter,
    shouldFetchFilteredResults,
    isFilterResetting,
  };
};
